import React from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { applyTimeZone } from '../../../utils/utils';
import { getCastellerDisplayName } from '../../../utils/castellerName';

// Register Chart.js and the annotation plugin
Chart.register(...registerables, annotationPlugin);

// Assign consistent colors to authors based on their name
const generateColorFromString = (str) => {
    let hash1 = 0;
    let hash2 = 0;
    let hash3 = 0;

    for (let i = 0; i < str.length; i++) {
        hash1 = str.charCodeAt(i) + ((hash1 << 7) - hash1);
        hash2 = str.charCodeAt(i) + ((hash2 << 11) - hash2);
        hash3 = str.charCodeAt(i) + ((hash3 << 13) - hash3);
    }

    const r = Math.abs((hash1 ^ (hash2 >> 13) ^ (hash3 << 7)) % 256);
    const g = Math.abs((hash2 ^ (hash3 >> 11) ^ (hash1 << 5)) % 256);
    const b = Math.abs((hash3 ^ (hash1 >> 17) ^ (hash2 << 3)) % 256);

    return `rgba(${r}, ${g}, ${b}, 0.8)`;
};

const AuthorHistogram = ({ histogramData, nHours, castellersInfo, events }) => {

    const decodeBase64 = (encodedString) => {
        const uint8Array = Uint8Array.from(atob(encodedString), c => c.charCodeAt(0));
        const decodedString = new TextDecoder().decode(uint8Array);
        return decodedString;
    }

    const chartData = React.useMemo(() => {
        if (!histogramData) return { labels: [], datasets: [] };

        const { bucketStarts, histogram, numBuckets, bucketMinutes } = histogramData;

        // Generate readable labels from bucket start timestamps
        const labels = bucketStarts.map(iso => {
            const d = new Date(iso);
            const day = d.getDate();
            const month = d.getMonth() + 1;
            const hour = d.getHours();
            const minute = d.getMinutes().toString().padStart(2, '0');
            if (bucketMinutes >= 1440) return `${day}/${month}`;
            return `${day}/${month} ${hour}:${minute}`;
        });

        // Build datasets from sparse histogram
        const datasets = Object.keys(histogram).map(author => {
            const authorBuckets = histogram[author];
            const dataArray = new Array(numBuckets).fill(0);
            for (const [idx, count] of Object.entries(authorBuckets)) {
                dataArray[parseInt(idx)] = count;
            }

            const label = castellersInfo[author]?.mote
                ? castellersInfo[author].mote
                : author in castellersInfo
                    ? getCastellerDisplayName(castellersInfo[author])
                    : author;

            return {
                label,
                data: dataArray,
                backgroundColor: generateColorFromString(author),
            };
        });

        return { labels, datasets };
    }, [histogramData, castellersInfo]);

    // Process events to get the start times within the selected nHours
    const eventsWithinNHours = events.filter(event => {
        const eventDate = applyTimeZone(event['data-esperada-inici']).toDate();
        const nHoursAgo = new Date(Date.now() - nHours * 60 * 60 * 1000);
        return new Date() > eventDate && eventDate > nHoursAgo;
    })

    const bucketMinutes = histogramData?.bucketMinutes;

    const eventStartTimes = eventsWithinNHours
        .map(event => {
            const eventDate = applyTimeZone(event['data-esperada-inici']).toDate();
            const day = eventDate.getDate();
            const month = eventDate.getMonth() + 1;
            const hour = eventDate.getHours();
            const minute = eventDate.getMinutes().toString().padStart(2, '0');
            const value = bucketMinutes >= 1440
                ? `${day}/${month}`
                : `${day}/${month} ${hour}:${minute}`;
            return {
                value,
                time: eventDate.getTime(),
                ...event
            };
        });


    // Sort events by time
    eventStartTimes.sort((a, b) => a.time - b.time);

    const eventsAnnotations = eventStartTimes.map(event => ({
        id: `event-${event.id}`,
        type: 'line',
        mode: 'vertical',
        scaleID: 'x',
        value: event.value,
        borderColor: 'red',
        borderWidth: 2,
        label: {
            rotation: 'auto',
            content: decodeBase64(event.title),
            display: true,
            position: 'start', // This will put the label at the top of the line
            font: {
                size: 6
            },
        },
    }))

    // Chart options with annotations
    const chartOptions = {
        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true
            }
        },
        maintainAspectRatio: false,
        plugins: {
            annotation: {
                annotations: {
                    ...eventsAnnotations
                }
            },
            datalabels: {
                display: false
            }
        }
    };

    const hasData = chartData.datasets.length > 0;

    return (
        <>
            <div style={{ height: '400px', marginBottom: 75 }}>
                <h2>Activitat de cada membre durant les últimes {nHours} hores</h2>
                {hasData ? (
                    <Bar
                        data={chartData}
                        options={chartOptions}
                    />
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#6b7280',
                        fontSize: '18px',
                        fontStyle: 'italic',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px dashed #d1d5db',
                    }}>
                        Cap canvi en les últimes {nHours} hores
                    </div>
                )}
            </div>
        </>
    );
};

export default AuthorHistogram;
